import axios from "axios";

import { API_BASE_URL } from "../apiBaseUrl";
import axiosInstance from "../axiosConfig";
import type {
  StudentAssignment,
  StudentCalendarResponse,
  StudentCatalogCourse,
  StudentCourse,
  StudentCourseOrder,
  StudentDocumentStatus,
  StudentLesson,
  StudentLinkDetail,
  StudentLegalState,
  StudentRoleOption,
  StudentSupportResponse,
  StudentTeacher,
  StudentTeacherDetails,
  StudentTeacherTelegramLink,
  StudentTestAnswer,
} from "./studentPortalService.types";

export const fetchPublicTeacher = async (slug: string): Promise<Omit<StudentTeacher, "id">> => {
  const response = await axios.get(`${API_BASE_URL}/student-web/public/teachers/${slug}/`);
  return response.data;
};

export const addTeacherCatalog = async (slug: string): Promise<{ created: boolean; teacher: StudentTeacher }> => {
  const response = await axiosInstance.post("student-web/catalog-access/", { slug });
  return response.data;
};

export const fetchStudentCatalogs = async (): Promise<StudentTeacher[]> => {
  const response = await axiosInstance.get("student-web/catalogs/");
  return response.data;
};

export const fetchStudentTeachers = async (): Promise<StudentTeacherDetails[]> => {
  const response = await axiosInstance.get("student-web/teachers/");
  return response.data;
};

export const createStudentTeacherTelegramLink = async (teacherId: number): Promise<StudentTeacherTelegramLink> => {
  const response = await axiosInstance.post(`student-web/teachers/${teacherId}/telegram-link/`);
  return response.data;
};

export const fetchStudentSupport = async (): Promise<StudentSupportResponse> => {
  const response = await axiosInstance.get("student-web/support/");
  return response.data;
};

export const fetchStudentCatalog = async (): Promise<StudentCatalogCourse[]> => {
  const response = await axiosInstance.get("student-web/catalog/");
  return response.data;
};

export const fetchStudentRoles = async (): Promise<StudentRoleOption[]> => {
  const response = await axiosInstance.get("student-web/roles/");
  return response.data;
};

export const submitCourseApplication = async (payload: {
  course: number;
  role: string;
  name: string;
  surname: string;
  phone: string;
  email: string;
  patronymic?: string;
  accepted_document_ids?: number[];
}): Promise<{ id: number; message: string }> => {
  const response = await axiosInstance.post("student-web/applications/", payload);
  return response.data;
};


export const fetchStudentApplicationLegalState = async (): Promise<StudentLegalState> => {
  const response = await axiosInstance.get("student-web/applications/legal-state/");
  return response.data;
};

export const fetchStudentMarketingConsent = async (): Promise<StudentLegalState> => {
  const response = await axiosInstance.get("student-web/marketing-consent/");
  return response.data;
};

export const setStudentMarketingConsent = async (documentId: number, enabled: boolean): Promise<void> => {
  await axiosInstance.post("student-web/marketing-consent/", { document_id: documentId, enabled });
};

export const fetchStudentCourses = async (): Promise<StudentCourse[]> => {
  const response = await axiosInstance.get("student-web/courses/");
  return response.data;
};

export const fetchStudentCourseOrders = async (courseId: number): Promise<StudentCourseOrder[]> => {
  const response = await axiosInstance.get(`student-web/courses/${courseId}/orders/`);
  return response.data;
};

export const fetchStudentLessons = async (courseId: number): Promise<StudentLesson[]> => {
  const response = await axiosInstance.get(`student-web/courses/${courseId}/lessons/`);
  return response.data;
};

export const fetchStudentLesson = async (courseId: number, lessonId: number): Promise<StudentLesson> => {
  const response = await axiosInstance.get(`student-web/courses/${courseId}/lessons/${lessonId}/`);
  return response.data;
};

export const fetchStudentAssignments = async (lessonId: number): Promise<StudentAssignment[]> => {
  const response = await axiosInstance.get(`student-web/lessons/${lessonId}/assignments/`);
  return response.data;
};

export const fetchStudentAssignment = async (assignmentId: number): Promise<StudentAssignment> => {
  const response = await axiosInstance.get(`student-web/assignments/${assignmentId}/`);
  return response.data;
};

const appendStudentResponseExtras = (
  formData: FormData,
  payload: { text?: string; video_url?: string; files?: File[] },
) => {
  if (payload.text) formData.append("text", payload.text);
  if (payload.video_url) formData.append("video_url", payload.video_url);
  (payload.files || []).forEach((file) => formData.append("files", file));
};

export const submitStudentAssignment = async (
  assignmentId: number,
  payload: { text?: string; video_url?: string; files?: File[] },
): Promise<StudentAssignment> => {
  const formData = new FormData();
  appendStudentResponseExtras(formData, payload);
  const response = await axiosInstance.post(
    `student-web/assignments/${assignmentId}/response/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

export const submitStudentAssignmentTest = async (
  assignmentId: number,
  payload: {
    answers: StudentTestAnswer[];
    text?: string;
    video_url?: string;
    files?: File[];
  },
): Promise<StudentAssignment> => {
  const formData = new FormData();
  formData.append("test_answers", JSON.stringify(payload.answers));
  appendStudentResponseExtras(formData, payload);
  const response = await axiosInstance.post(
    `student-web/assignments/${assignmentId}/test-response/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

export const fetchStudentCalendar = async (year: number, month: number): Promise<StudentCalendarResponse> => {
  const response = await axiosInstance.get("student-web/calendar/", { params: { year, month } });
  return response.data;
};

export const fetchStudentDocument = async (courseId: number): Promise<StudentDocumentStatus> => {
  const response = await axiosInstance.get(`student-web/courses/${courseId}/document/`);
  return response.data;
};

export const fetchStudentLinkDetail = async (token: string): Promise<StudentLinkDetail> => {
  const response = await axios.get(`${API_BASE_URL}/student-links/${token}/`);
  return response.data;
};

export const attachExistingStudentAccount = async (
  token: string,
  email: string,
  password: string,
): Promise<{ student_id: number; email: string; message: string }> => {
  const response = await axios.post(`${API_BASE_URL}/student-links/${token}/attach-existing/`, {
    email,
    password,
  });
  return response.data;
};

export const buildLessonPlayerUrl = (lessonId: number, videoId: number): string =>
  `/player/?lesson=${lessonId}&video_id=${videoId}&v=20261014-direct`;

export const buildAssignmentPlayerUrl = (assignmentId: number, videoId: number): string =>
  `/player/?assignment=${assignmentId}&video_id=${videoId}&v=20261014-direct`;